import { useEffect, useRef, useState } from 'react'
import { useAccessPolicies, useCreateAccessPolicy, useSourceReadProfile, useSetSourceReadProfile } from '@/hooks/useAccessManagement'
import { ApiError } from '@/lib/api'
import { SOURCE_RESOURCE_ENGINE, SOURCE_RESOURCE_KIND } from '@/lib/access-policy-presets'
import { SOURCE_RESOURCE_IDS } from '@/lib/source-resources'
import type { SourceReadProfile, SourceReadProfileState, AccessPolicy, AccessPoliciesListResponse } from '@/types/api'
import type { CanonicalWorkspacePrincipal } from '@/types/gift-operations'

interface Props {
  workspaceId: string
  operator: CanonicalWorkspacePrincipal
  operators: readonly CanonicalWorkspacePrincipal[]
  operatorsReady: boolean
}

const buttonClass = 'min-h-9 rounded-md border px-3 py-1 text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50'

function canonicalOwner({ workspaceId, operator, operators, operatorsReady }: Props): string | null {
  const identity = operator.workspaceIdentity
  const slot = identity.dataUserSlot?.trim()
  const policyRef = operator.accessPolicyPrincipal.principalRef
  if (!operatorsReady || !workspaceId || operator.workspaceId !== workspaceId
    || identity.state !== 'active' || !identity.memberId || !identity.sessionUserId?.trim()
    || !Array.isArray(identity.conflicts) || identity.conflicts.length > 0
    || !slot || slot === '__legacy_meeting_deny__'
    || operator.accessPolicyPrincipal.state === 'conflict'
    || (policyRef !== null && policyRef !== slot)) return null
  const active = operators.filter((entry) => entry.workspaceId === workspaceId && entry.workspaceIdentity.state === 'active')
  if (active.filter((entry) => entry.workspaceIdentity.dataUserSlot?.trim() === slot).length !== 1
    || active.filter((entry) => entry.workspaceIdentity.sessionUserId === identity.sessionUserId).length !== 1
    || !active.some((entry) => entry.workspaceIdentity.dataUserSlot?.trim() === slot
      && entry.workspaceIdentity.sessionUserId === identity.sessionUserId && entry.workspaceIdentity.memberId === identity.memberId)) return null
  return slot
}

/** These are individual settings only; effective rights remain the resolver's responsibility. */
function readPolicies(data: AccessPoliciesListResponse | undefined, workspaceId: string): AccessPolicy[] | null {
  if (!data || !Array.isArray(data.policies) || data.total !== data.policies.length || data.total >= 500) return null
  if (data.policies.some((policy) => !policy || typeof policy !== 'object' || policy.workspaceId !== workspaceId
    || policy.accessScope !== 'individual' || !policy.id || !policy.principalRef
    || !policy.resourceKind || !policy.resourceId || !policy.dataClass
    || !Array.isArray(policy.actions) || policy.actions.length === 0
    || !policy.actions.every((action) => ['view', 'edit', 'delete'].includes(action))
    || !['allow', 'deny'].includes(policy.effect))) return null
  return data.policies
}

function isSourcePolicy(policy: AccessPolicy): boolean {
  return policy.resourceEngine === SOURCE_RESOURCE_ENGINE
    && policy.resourceKind === SOURCE_RESOURCE_KIND && policy.dataClass === 'tool_connection'
}

function individualSetting(policies: AccessPolicy[], principalRef: string, resourceId: string) {
  const matching = policies.filter((policy) => policy.resourceEngine === SOURCE_RESOURCE_ENGINE
    && policy.principalRef === principalRef && (policy.resourceId === resourceId || policy.resourceId.includes('*')))
  if (matching.some((policy) => policy.effect === 'deny')) return '既存の拒否設定あり'
  if (matching.length === 0) return '未登録'
  if (matching.length === 1 && isSourcePolicy(matching[0]!) && matching[0]!.resourceId === resourceId
    && matching[0]!.actions.length === 1 && matching[0]!.actions[0] === 'view' && matching[0]!.effect === 'allow') return '本人の閲覧許可あり'
  return '別の個別設定あり（ここでは変更不可）'
}

export function OperatorSourceAccess(props: Props) {
  const identity = props.operator.workspaceIdentity
  return <SourceAccessDisclosure key={`${props.workspaceId}:${identity.sessionUserId}:${identity.dataUserSlot}`} {...props} />
}

function SourceAccessDisclosure(props: Props) {
  const [open, setOpen] = useState(false)
  const principalRef = canonicalOwner(props)
  return (
    <section className="mt-4 border-t pt-3" aria-label="本人の情報源閲覧設定">
      <button type="button" aria-expanded={open} className={buttonClass} onClick={() => setOpen(!open)}>情報源の閲覧</button>
      {open && (principalRef ? (
        <><ReadProfileSettings workspaceId={props.workspaceId} principalUserId={props.operator.workspaceIdentity.sessionUserId!} />
        <SourceAccessSettings workspaceId={props.workspaceId} principalRef={principalRef} /></>
      ) : <p role="status" className="mt-2 text-sm text-muted-foreground">本人と対象の会社の一致を確認できません。情報源の設定変更は停止しています。</p>)}
    </section>
  )
}

const READ_PROFILE_LABELS: Record<SourceReadProfile, string> = {
  all_sources_all_fieldy: '全情報を閲覧',
  all_sources_own_fieldy: 'Fieldyは本人分、他は全情報',
  configured_without_fieldy: '個別設定に従う（Fieldy不可）',
}
function validProfileState(value: SourceReadProfileState | undefined): value is SourceReadProfileState {
  return Boolean(value && (value.profile === null || Object.hasOwn(READ_PROFILE_LABELS, value.profile))
    && ['all', 'own', 'none'].includes(value.effectiveFieldyScope) && Array.isArray(value.conflicts)
    && value.conflicts.every((conflict) => typeof conflict === 'string'))
}
function ReadProfileSettings({ workspaceId, principalUserId }: { workspaceId: string; principalUserId: string }) {
  const query = useSourceReadProfile(workspaceId, principalUserId)
  const mutation = useSetSourceReadProfile(workspaceId, principalUserId)
  const [selected, setSelected] = useState<SourceReadProfile | ''>('')
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const mounted = useRef(true)
  const [message, setMessage] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const known = !query.isError && !query.isLoading && !query.isFetching && validProfileState(query.data)
  const state = known ? query.data : undefined
  async function refresh() {
    if (inFlight.current) return
    inFlight.current = true; setBusy(true)
    try {
      const result = await query.refetch()
      if (!mounted.current) return
      if (result.isError || !validProfileState(result.data)) throw new Error('unknown')
      setFailed(false); setMessage(null)
    } catch { if (mounted.current) { setFailed(true); setMessage('閲覧範囲を再取得できません。変更は停止しています。') } }
    finally { inFlight.current = false; if (mounted.current) setBusy(false) }
  }
  async function save() {
    if (inFlight.current || !known || !selected || failed) return
    inFlight.current = true; setBusy(true); setMessage(null)
    try {
      const before = await query.refetch()
      if (!mounted.current) return
      if (before.isError || !validProfileState(before.data)) throw new Error('unknown')
      await mutation.mutateAsync(selected)
      if (!mounted.current) return
      const after = await query.refetch()
      if (!mounted.current) return
      if (after.isError || !validProfileState(after.data) || after.data.profile !== selected || after.data.conflicts.length) throw new Error('unconfirmed')
      setSelected(''); setMessage('閲覧範囲を保存し、実際に適用される設定を再確認しました。')
    } catch (error) {
      if (mounted.current) {
        setFailed(true)
        setMessage(error instanceof ApiError && error.status === 409
          ? '既存の拒否設定、または閲覧と編集を兼ねた設定があるため保存できません。設定を再取得してください。'
          : '保存結果を確認できません。設定を再取得するまで変更を停止します。')
      }
    } finally { inFlight.current = false; if (mounted.current) setBusy(false) }
  }
  return <div className="mt-3 space-y-3" aria-label="閲覧範囲の設定">
    <p className="text-sm">情報の閲覧範囲を設定します。編集、削除、外部送信の権限は変更しません。</p>
    <p className="text-sm">保存済みの閲覧範囲: {state ? state.profile ? READ_PROFILE_LABELS[state.profile] : '未設定' : '未確認'}</p>
    {state && <p className="text-sm">現在のFieldy閲覧範囲: {{ all: '全員分', own: '本人分のみ', none: '閲覧不可' }[state.effectiveFieldyScope]}</p>}
    {state && state.conflicts.length > 0 && <p role="status" className="text-sm">保存済みの範囲と適用中の権限に不一致があります。</p>}
    {!state && <p role="status" className="text-sm">{query.error instanceof ApiError && query.error.status === 403 ? '閲覧範囲の変更には、この会社の管理者の権限が必要です。保存は停止しています。' : '現在の閲覧範囲を確認できるまで保存を停止します。'}</p>}
    <label className="block text-sm">変更する閲覧範囲
      <select aria-label="変更する閲覧範囲" className="ml-2 rounded-md border p-2" value={selected}
        disabled={busy || !known || failed} onChange={(event) => setSelected(event.target.value as SourceReadProfile | '')}>
        <option value="">選択してください</option>
        {Object.entries(READ_PROFILE_LABELS).map(([profile, label]) => <option key={profile} value={profile}>{label}</option>)}
      </select>
    </label>
    <button type="button" className={buttonClass} disabled={busy || mutation.isPending || !known || !selected || failed} onClick={() => void save()}>閲覧範囲を保存</button>
    <button type="button" className={buttonClass} disabled={busy || query.isFetching} onClick={() => void refresh()}>閲覧範囲を再取得</button>
    {message && <p role={failed ? 'alert' : 'status'} className="text-sm">{message}</p>}
  </div>
}

function SourceAccessSettings({ workspaceId, principalRef }: { workspaceId: string; principalRef: string }) {
  const query = useAccessPolicies(workspaceId)
  const createPolicy = useCreateAccessPolicy(workspaceId)
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  const mounted = useRef(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const policies = !query.isError && !query.isLoading && !query.isFetching ? readPolicies(query.data, workspaceId) : null
  const layerExists = policies?.some(isSourcePolicy) ?? false
  const unavailableMessage = query.error instanceof ApiError && query.error.status === 403
    ? '情報源の設定には、この会社の管理者の権限が必要です。変更は停止しています。'
    : '現在の個別設定を確認できません。未登録とは扱わず、再取得するまで変更を停止します。'

  async function refresh() {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    try {
      const fresh = await query.refetch()
      if (!mounted.current) return
      if (fresh.isError || !readPolicies(fresh.data, workspaceId)) throw new Error('settings unknown')
      setErrorMessage(null)
      setStatusMessage(null)
    } catch {
      if (mounted.current) setErrorMessage('設定を再取得できませんでした。変更は停止しています。')
    } finally {
      inFlight.current = false
      if (mounted.current) setBusy(false)
    }
  }

  async function allow(resourceId: string) {
    if (inFlight.current || errorMessage || !policies || !layerExists
      || individualSetting(policies, principalRef, resourceId) !== '未登録') return
    inFlight.current = true
    setBusy(true)
    setStatusMessage(null)
    try {
      const before = await query.refetch()
      if (!mounted.current) return
      const current = !before.isError ? readPolicies(before.data, workspaceId) : null
      if (!current) throw new Error('settings unknown')
      if (!current.some(isSourcePolicy) || individualSetting(current, principalRef, resourceId) !== '未登録') {
        setStatusMessage('設定が更新されています。最新の個別設定を確認してください。')
        return
      }
      await createPolicy.mutateAsync({
        resourceEngine: SOURCE_RESOURCE_ENGINE, resourceKind: SOURCE_RESOURCE_KIND,
        dataClass: 'tool_connection', principalRef, resourceId, actions: ['view'], effect: 'allow',
      })
      if (!mounted.current) return
      const after = await query.refetch()
      if (!mounted.current) return
      const saved = !after.isError ? readPolicies(after.data, workspaceId) : null
      if (!saved || individualSetting(saved, principalRef, resourceId) !== '本人の閲覧許可あり') throw new Error('save unconfirmed')
      setStatusMessage(`${resourceId} の本人閲覧許可を保存しました。実際の閲覧範囲は接続後に確認してください。`)
    } catch (error) {
      if (mounted.current) setErrorMessage(error instanceof ApiError && error.status === 403
        ? 'この会社の管理者の権限を確認してください。追加結果は再取得するまで未確認です。'
        : '追加結果を確認できません。再取得してから次の操作を判断してください。')
    } finally {
      inFlight.current = false
      if (mounted.current) setBusy(false)
    }
  }

  return (
    <div className="mt-3 space-y-3">
      <p className="text-sm text-muted-foreground">本人に直接付けた閲覧設定を表示します。役割や部署の設定も適用されるため、実際の閲覧範囲は接続後に確認してください。</p>
      <button type="button" className={buttonClass} disabled={busy || query.isFetching} onClick={() => void refresh()}>個別設定を再取得</button>
      {!policies && <p role="status" className="text-sm text-muted-foreground">{unavailableMessage}</p>}
      {policies && !layerExists && <p role="status" className="text-sm text-muted-foreground">情報源の閲覧設定が導入済みか確認できません。最初の設定は他の利用者にも影響するため、この画面では追加しません。</p>}
      {errorMessage && <p role="alert" className="text-sm text-destructive">{errorMessage}</p>}
      {statusMessage && <p role="status" className="text-sm">{statusMessage}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead><tr><th className="py-2">情報源</th><th>本人の個別設定</th><th>操作</th></tr></thead>
          <tbody>{SOURCE_RESOURCE_IDS.map((sourceId) => {
            const setting = policies && !errorMessage ? individualSetting(policies, principalRef, sourceId) : '未確認'
            return <tr key={sourceId} className="border-t">
              <th className="py-2 font-medium" scope="row">{sourceId}</th>
              <td>{setting}</td>
              <td><button type="button" className={buttonClass} aria-label={`${sourceId} の本人閲覧を許可`}
                disabled={busy || createPolicy.isPending || !layerExists || setting !== '未登録'}
                onClick={() => void allow(sourceId)}>閲覧を許可</button></td>
            </tr>
          })}</tbody>
        </table>
      </div>
    </div>
  )
}
